import { useState } from 'react';
import { MeasurementPlan } from '../../../shared/types/protocol.ts';
import { Protocol, ProtocolDraft } from '../../types/sharedTypeImpl.ts';
import { formatDatetime } from '../../utils/fieldFormats.ts';
import { CustomerSelect } from '../customerSelect.tsx';
import { IntegerInput } from '../integerInput.tsx';
import { LinkButton } from '../linkButton.tsx';
import { MeasurementPlanDialog } from '../measurement-plan-dialog/measurementPlanDialog.tsx';
import { OperatorSelect } from '../operatorSelect.tsx';
import { AllowedMachinesDialog } from '../allowedMachinesDialog.tsx';
import { MachineBadges } from '../machineBadges.tsx';
import { trpc } from '../../utils/trpc/trpc.ts';
import { ProtocolVersionsDialog } from '../protocolVersionsDialog.tsx';
import { toleranceStandards } from './generalTolerances.ts';
import { ProgramNamesList } from './programNamesList.tsx';


interface Props {
    protocol: Protocol;
    readOnly?: boolean;
    disabled?: boolean;
    collapseMinorFields?: boolean;
    setProtocolField?: (field: keyof Protocol, value: string | string[] | number | null) => void;
    deleteProtocolField?: (field: keyof Protocol) => void;
    onSetPdfFile?: (file: File) => void;
    onMeasurementPlanOpened?: () => void;
    onSubmitMeasurementPlan?: (measurementPlan: MeasurementPlan, interval: number | null) => void;
}

export function ProtocolMetaFields({
    protocol,
    readOnly,
    disabled,
    collapseMinorFields,
    setProtocolField,
    deleteProtocolField,
    onSetPdfFile,
    onSubmitMeasurementPlan,
}: Props) {

    const [isVersionsDialogOpen, setIsVersionsDialogOpen] = useState(false);
    const [isMeasurementPlanOpen, setIsMeasurementPlanOpen] = useState(false);
    const [isMachinesOpen, setIsMachinesOpen] = useState(false);
    const { data: equipments } = trpc.equipments.getAllCustom.useQuery({ type: 'cmm/vmm' });

    const editEnabled = !readOnly && !disabled;
    if (editEnabled && (!setProtocolField || !deleteProtocolField || !onSetPdfFile || !onSubmitMeasurementPlan)) {
        throw new Error('All setters must be provided when not readonly or disabled');
    }

    const handlePdfFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;
        onSetPdfFile!(file);
    }

    const handleTextChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setProtocolField!(event.target.name as keyof ProtocolDraft, event.target.value);
    }

    const handleProgramNamesChange = (programNames: string[]) => {
        setProtocolField!('programs', programNames);
    }

    const handleTolerancSelect = (event: React.ChangeEvent<HTMLSelectElement>) => {
        if (!event.target.value) {
            return;
        }

        setProtocolField!('generalTolerance', event.target.value);
    }

    const handleMeasureIntervalChange = (value: number | null) => {
        if (value) {
            setProtocolField!('measureInterval', value);
        } else {
            deleteProtocolField!('measureInterval');
        }
    }

    const handleMeasurementPlanOpen = () => {
        setIsMeasurementPlanOpen(true);
        deleteProtocolField?.('requiresPlanReview');
    }


    const showMinor = !collapseMinorFields;
    return (
        <>
            {editEnabled && <tr>
                <td>Pdf file</td>
                <td>
                    <input
                        type='file'
                        accept='.pdf'
                        onClick={(e) => readOnly ? e.preventDefault() : null}
                        onChange={handlePdfFileChange}
                        readOnly={readOnly}
                        disabled={disabled}
                    />
                </td>
            </tr>}

            <tr>
                <td>Part number</td>
                <td>
                    <input
                        type='text'
                        name='partName'
                        value={protocol.partName ?? ''}
                        onChange={handleTextChange}
                        readOnly={readOnly}
                        disabled={disabled}
                    />
                </td>
            </tr>

            <tr>
                <td>Revision</td>
                <td>
                    <input
                        type='text'
                        name='partRevision'
                        value={protocol.partRevision ?? ''}
                        onChange={handleTextChange}
                        readOnly={readOnly}
                        disabled={disabled}
                    />
                </td>
            </tr>

            {(!editEnabled && protocol.version && showMinor) && <tr>
                <td>Protocol version</td>
                <td>
                    <input
                        type='text'
                        value={protocol.version}
                        readOnly={true}
                        disabled={disabled}
                    />
                </td>
            </tr>}

            {showMinor && <tr>
                <td>Program names</td>
                <td>
                    {editEnabled
                        ? <ProgramNamesList
                            programNames={protocol.programs ?? []}
                            setProgramNames={handleProgramNamesChange}
                            readOnly={readOnly}
                        />
                        : <textarea
                            name='programs'
                            value={protocol.programs.join('\n')}
                            rows={protocol.programs.length}
                            readOnly={readOnly}
                            disabled={disabled}
                        />
                    }
                </td>
            </tr>}

            <tr>
                <td>General tolerance</td>
                <td>
                    <select
                        style={{ pointerEvents: readOnly ? 'none' : 'auto' }}
                        value={protocol.generalTolerance ?? ''}
                        onChange={handleTolerancSelect}
                        disabled={disabled}
                    >
                        <option value='' />
                        {toleranceStandards.map(it =>
                            <option key={it} value={it}>{it}</option>
                        )}
                    </select>
                </td>
            </tr>

            {showMinor && <tr>
                <td>Customer</td>
                <td>
                    <CustomerSelect
                        name={protocol.customer}
                        onChange={(name) => setProtocolField!('customer', name)}
                        readOnly={readOnly}
                        disabled={disabled}
                    />
                </td>
            </tr>}

            {showMinor && <tr>
                <td>Created by</td>
                <td>
                    <OperatorSelect
                        operatorInitials={protocol.user}
                        onChange={(initials) => setProtocolField!('user', initials)}
                        readOnly={readOnly}
                        disabled={disabled}
                        required
                    />
                </td>
            </tr>}

            {(!editEnabled && protocol.createTime && showMinor) && <tr>
                <td>Protocol create time</td>
                <td>
                    <input
                        type='text'
                        value={formatDatetime(protocol.createTime)}
                        readOnly={true}
                        disabled={disabled}
                    />
                </td>
            </tr >}

            {showMinor && <tr>
                <td>
                    <LinkButton
                        children='Latest changes'
                        onClick={() => setIsVersionsDialogOpen(true)}
                        disabled={!protocol.partName || !protocol.partRevision}
                    />
                </td>
                <td>
                    <textarea
                        name='changes'
                        value={protocol.changes ?? ''}
                        rows={3}
                        onChange={handleTextChange}
                        readOnly={readOnly}
                        disabled={disabled}
                    />
                </td>
            </tr>}

            {showMinor && <tr>
                <td>Comment</td>
                <td>
                    <textarea
                        name='comment'
                        value={protocol.comment ?? ''}
                        rows={3}
                        onChange={handleTextChange}
                        readOnly={readOnly}
                        disabled={disabled}
                    />
                </td>
            </tr>}

            <tr>
                <td>Allowed machines</td>
                <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <MachineBadges
                            machineIds={protocol.allowedMachines ?? []}
                            equipments={equipments}
                        />
                        {editEnabled && <button
                            type='button'
                            style={{ marginLeft: 'auto', flexShrink: 0 }}
                            onClick={() => setIsMachinesOpen(true)}
                        >
                            Edit
                        </button>}
                    </div>
                </td>
            </tr>

            <tr>
                <td>Measurement interval</td>
                <td>
                    <div style={{ display: 'flex', flexDirection: 'row' }}>
                        <IntegerInput
                            style={{ flexGrow: 1 }}
                            value={protocol.measureInterval ?? null}
                            onChange={handleMeasureIntervalChange}
                            disabled={disabled}
                        />

                        <button
                            type='button'
                            onClick={handleMeasurementPlanOpen}
                            disabled={!editEnabled && !protocol.measurementPlan}
                            children={protocol.measurementPlan || !editEnabled
                                ? 'Measurement plan'
                                : 'Create measurement plan'
                            }
                        />
                    </div>
                </td>
            </tr>

            {isMachinesOpen && <AllowedMachinesDialog
                machineIds={protocol.allowedMachines ?? []}
                equipments={equipments}
                onSubmit={machines => setProtocolField!('allowedMachines', machines)}
                onClose={() => setIsMachinesOpen(false)}
            />}

            <ProtocolVersionsDialog
                isOpen={isVersionsDialogOpen}
                protocol={protocol}
                onClose={() => setIsVersionsDialogOpen(false)}
            />

            {isMeasurementPlanOpen && <MeasurementPlanDialog
                protocol={protocol}
                isViewMode={(readOnly || disabled) ?? false}
                onSubmit={onSubmitMeasurementPlan!}
                onClose={() => setIsMeasurementPlanOpen(false)}
            />}
        </>
    );
}
